import React from "react";
import { Calendar, User, Tag, Clock } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="2034" className="container mx-auto px-4 py-20 max-w-4xl animate-pulse">
        <div className="h-10 bg-muted w-3/4 mb-6"></div>
        <div className="flex gap-4 mb-8">
          <div className="h-4 bg-muted w-24"></div>
          <div className="h-4 bg-muted w-24"></div>
        </div>
        <div className="h-96 bg-muted mb-10"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted w-full"></div>
          <div className="h-4 bg-muted w-full"></div>
          <div className="h-4 bg-muted w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="bg-destructive/10 text-destructive p-4 inline-block font-serif italic">
          Sacred transmission interrupted: {error}
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground text-lg font-serif italic mb-6">The transmission was not found in this realm.</p>
        <Link to="#">
          <Button className="mt-4 font-default tracking-widest uppercase">Return to Sanctuary</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      {}
      <header className="mb-10 text-center md:text-left">
        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight tracking-tighter font-default uppercase">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-muted-foreground text-sm md:text-base font-serif italic">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
          )}
          
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              <span>{post.author.name}</span>
            </div>
          )}

          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary" />
              <div className="flex flex-wrap gap-2">
                {post.categories.map((cat, i) => (
                  <span 
                    key={cat.id} 
                    data-index={i}
                    className="bg-secondary text-secondary-foreground px-3 py-0.5 text-[10px] font-default font-bold uppercase tracking-widest"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden shadow-2xl bg-muted ring-1 ring-border/50">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[700px] grayscale-[20%] hover:grayscale-0 transition-all duration-1000"
          />
        </div>
      )}

      {}
      <article 
        className="prose prose-lg dark:prose-invert max-w-none mb-16 text-foreground/90 leading-relaxed font-serif [&_p]:mb-8 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:font-default [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-foreground [&_h2]:uppercase [&_h2]:tracking-tight [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:font-default [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-foreground [&_h3]:tracking-tight [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-8 [&_li]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-8 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-8 hover:[&_a]:text-primary/70 transition-colors [&_blockquote]:border-l-2 [&_blockquote]:border-[#a855f7] [&_blockquote]:pl-8 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-12 [&_blockquote]:bg-accent/5 [&_blockquote]:py-4 [&_img]:rounded-none [&_img]:shadow-2xl [&_img]:my-12 [&_img]:w-full [&_figure]:my-12 [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:text-muted-foreground [&_figcaption]:mt-4 [&_figcaption]:italic"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.author && (
        <div className="border-t border-border/30 pt-10 mt-10">
          <Card className="bg-card/30 border-border/30 shadow-2xl overflow-hidden backdrop-blur-sm">
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8 p-10">
                <div className="shrink-0">
                  {post.author.avatar ? (
                    <img
                      src={post.author.avatar["96"] || Object.values(post.author.avatar)[0]}
                      alt={post.author.name}
                      className="w-28 h-28 border border-[#a855f7] shadow-xl object-cover grayscale hover:grayscale-0 transition-all duration-700"
                    />
                  ) : (
                    <div className="w-28 h-28 bg-accent/10 flex items-center justify-center text-primary">
                      <User className="w-12 h-12" />
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-3 font-default uppercase tracking-wider">
                    The Guide: {post.author.name}
                  </h3>
                  <p className="text-muted-foreground mb-8 leading-relaxed font-serif italic text-lg">
                    {}
                    Visionary curator and guide at Yoga Dinners. Dedicated to weaving movement, deep bass rhythms, and sacred connection into immersive ritual experiences that awaken the senses.
                  </p>
                  {post.author.link && (
                    <Link to={post.author.link}>
                      <Button variant="outline" size="sm" className="border-[#a855f7] hover:border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default uppercase tracking-widest text-xs h-10 px-6">
                        Explore the Guide's Journey
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default function SinglePostTemplate() {
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen w-full dark">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}